import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  RefreshCcw,
} from "lucide-react";
import useAxiosInstance from "../../helpers/useAxiosInstance";

const VideoPlayer = ({ data = "" }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({
    video: "",
    thumbnail: "",
  });

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const axiosInstance = useAxiosInstance()

  useEffect(() => {
    getVideo();
  }, [data]);

  useEffect(() => {
    if (videoRef && videoRef?.current && currentVideo?.video) {
      videoRef.current.src = currentVideo.video;
      videoRef.current.load();
    }
  }, [currentVideo.video]);

  const togglePlayPause = async () => {
    if (!videoRef?.current) return;
    try {
      if (videoRef?.current?.paused) {
        await videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    } catch (error) {
      console.error("Error toggling play/pause:", error);
    }
  };

  const toggleMute = () => {
    try {
      if (videoRef?.current) {
        videoRef.current.muted = !videoRef.current.muted;
        setIsMuted(!isMuted);
      }
    } catch (error) {
      console.error("Error toggling mute/unmute:", error);
    }
  };

  const toggleFullscreen = () => {
    try {
      const video = videoRef.current;
      if (!video) return;
      if (!document.fullscreenElement) {
        video.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    } catch (error) {
      console.error("Error toggling fullscreen", error);
    }
  };

  const seek = (e) => {
    try {
      if (!videoRef?.current) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const newTime =
        ((e?.clientX - rect?.left) / rect?.width) * videoRef?.current?.duration;
      videoRef.current.currentTime = newTime;
    } catch (error) {
      console.error("Error seeking video", error);
    }
  };

  const updateProgress = () => {
    try {
      if (videoRef?.current) {
        setCurrentTime(videoRef?.current?.currentTime || 0);
        setDuration(videoRef.current.duration || 0);
      }
    } catch (error) {
      console.error("Error updating video timer,error");
    }
  };

  const formatTime = (time) => {
    try {
      return new Date(time * 1000).toISOString().slice(14, 19);
    } catch (error) {
      console.error("Error formatting time", error);
    }
  };

  const handleSeeking = () => {
    try {
      if (!isPlaying) {
        setIsPlaying(true);
      }
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
        }
      }, 500);
    } catch (error) {
      console.error(`Error in handleSeeking function ::`, error);
    }
  };

  const getVideo = async () => {
    try {
      const encodedValue = data?.split("=")[1];
      const testParam = decodeURIComponent(atob(encodedValue));
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/playVideo`,
        { folderPath: testParam }
      );
      setCurrentVideo({
        video: res.data?.signedUrl || "",
        thumbnail: res.data?.signedUrlThumb || "",
      });
    } catch (err) {
      console.error("Error fetching video:", err);
      setCurrentVideo({ video: "", thumbnail: "" });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting && videoRef?.current) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.5 }
    );

    if (containerRef?.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef?.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full flex items-center justify-center rounded-xl"
    >
      <video
        ref={videoRef}
        key={currentVideo.video}
        playsInline
        // controls
        style={{ transform: `rotate(${rotation}deg)` }}
        className="w-full h-full max-h-72"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={updateProgress}
        onSeeking={handleSeeking}
      >
        <source src={currentVideo?.video} />
        Your browser does not support the video tag.
      </video>

      {/* Play Button Overlay */}
      {!isPlaying && (
        <div
          style={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          className="absolute flex flex-col items-center justify-center text-white text-3xl"
        >
          <button
            className="w-16 h-16 bg-black bg-opacity-30 rounded-full transition duration-300 hover:bg-opacity-50"
            onClick={togglePlayPause}
          >
            <svg
              width="64"
              height="64"
              viewBox="0 0 72 72"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="35.7789"
                cy="35.9322"
                r="34.5428"
                stroke="white"
                strokeWidth="2.4"
              />
              <path
                d="M52.3914 32.8145C54.7914 34.2002 54.7914 37.6643 52.3914 39.0499L30.1723 51.8781C27.7723 53.2638 24.7723 51.5317 24.7723 48.7604L24.7723 23.1041C24.7723 20.3328 27.7723 18.6007 30.1723 19.9864L52.3914 32.8145Z"
                stroke="white"
                strokeWidth="2.4"
              />
            </svg>
          </button>
          <span className="text-sm lg:text:lg mt-2 font-dmserif">
            Virtual Tour Preview
          </span>
        </div>
      )}

      {isPlaying && (
        <div className="absolute bottom-0 left-2 right-2 flex justify-between items-center text-[#596962] z-10 px-2">
          {/* Left controls: play/pause, mute/unmute */}
          <div className="flex items-center gap-2">
            <button onClick={togglePlayPause}>
              {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <button onClick={toggleMute}>
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>

          {/* Progress bar */}
          <div
            className="flex-1 mx-1 h-2 bg-gray-300 rounded cursor-pointer"
            onClick={seek}
          >
            <div
              className="h-full bg-[#596962] rounded"
              style={{
                width: `${(currentTime / duration) * 100 || 0}%`,
              }}
            />
          </div>

          {/* Time display */}
          {/* <span className="text-sm min-w-[80px] text-center">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span> */}

          {/* Right controls: rotate, fullscreen */}
          <div className="flex items-center pl-1 gap-2">
            <Tooltip title="Rotate Video">
              <button onClick={() => setRotation((prev) => (prev + 180) % 360)}>
                <RefreshCcw size={18} className="rotate-90" />
              </button>
            </Tooltip>
            <button onClick={toggleFullscreen}>
              <Maximize2 size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
